import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as AuthActions from './auth.actions';
import { AuthService } from '../../core/services/api/auth/auth.service';
import { mergeMap, map, catchError, of, tap } from 'rxjs';
import { Router } from '@angular/router';
import { ToastService } from '../../core/services/toast/toast.service';

@Injectable()
export class AuthEffects {
  private toast = inject(ToastService);
  private authService = inject(AuthService);
  private router = inject(Router);
  private actions$ = inject(Actions);

  // LOGIN
  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.login),
      mergeMap(({ identifier, password }) =>
        this.authService.login(identifier, password).pipe(
          mergeMap((response) => {
            return of(
              AuthActions.loginSuccess({
                user: response.data.user,
                token: response.data.token,
                fromApi: true,
              })
            );
          }),
          catchError(({ error }) => {
            if (error.meta?.status && error.meta.status >= 401) {
              return of(
                AuthActions.loginFailure({
                  error: error.meta.message,
                })
              );
            }

            if (error.meta?.status && error.meta.status >= 409) {
              return of(
                AuthActions.loginFailure({
                  error: error.meta.message,
                })
              );
            }

            if (error.meta?.status && error.meta.status >= 422) {
              return of(
                AuthActions.loginFailure({
                  error: error.meta.message,
                })
              );
            }

            return of(AuthActions.loginFailure({ error: error.meta.message ?? 'Erreur inconnue' }));
          })
        )
      )
    )
  );

  loginSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loginSuccess),
        tap(({ user, token, fromApi = false }) => {
          this.authService.setAccessToken(token);
          this.authService.setUserInfoToken(user);

          if (fromApi) {
            this.router.navigate(['/admin/dashboard']);
            this.toast.success('Connexion réussie');
          }
        })
      ),
    { dispatch: false }
  );

  loginFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loginFailure),
        tap(({ error }) => {
          this.toast.error(error ?? 'Échec de la connexion.');
        })
      ),
    { dispatch: false }
  );

  // REGISTER
  register$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.register),
      mergeMap(({ email, password, name }) =>
        this.authService.register(email, password, name).pipe(
          map((user) => AuthActions.registerSuccess({ user })),
          catchError((err) => of(AuthActions.registerFailure({ error: err.message })))
        )
      )
    )
  );

  registerSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.registerSuccess),
        tap(({ user }) => {
          this.router.navigate(['/login']);
          this.toast.success(`Votre compte "${user.firstName} ${user.lastName}" créé avec succès.`);
        })
      ),
    { dispatch: false }
  );

  // FORGOT PASSWORD
  forgotPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.forgotPassword),
      mergeMap(({ email }) =>
        this.authService.forgotPassword(email).pipe(
          map((res) => AuthActions.forgotPasswordSuccess({ message: res.message })),
          catchError((err) => of(AuthActions.forgotPasswordFailure({ error: err.message })))
        )
      )
    )
  );

  // RESET PASSWORD
  resetPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.resetPassword),
      mergeMap(({ token, password }) =>
        this.authService.resetPassword(token, password).pipe(
          map((res) => AuthActions.resetPasswordSuccess({ message: res.message })),
          catchError((err) => of(AuthActions.resetPasswordFailure({ error: err.message })))
        )
      )
    )
  );

  // VERIFY OTP
  verifyOtp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.verifyOtp),
      mergeMap(({ email, otp }) =>
        this.authService.verifyOtp(email, otp).pipe(
          map((user) => AuthActions.verifyOtpSuccess({ user })),
          catchError((err) => of(AuthActions.verifyOtpFailure({ error: err.message })))
        )
      )
    )
  );

  verifyOtpSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.verifyOtpSuccess),
        tap(() => {
          this.router.navigate(['/dashboard']);
        })
      ),
    { dispatch: false }
  );

  // LOGOUT
  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logout),
      mergeMap(() =>
        this.authService.logout().pipe(
          map(() => AuthActions.logoutSuccess()),
          catchError(() => {
            return of(AuthActions.logoutFailure());
          })
        )
      )
    )
  );

  logoutSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.logoutSuccess),
        tap(() => {
          this.authService.clearAuth();
          this.router.navigate(['/login']);
          this.toast.success('Déconnexion réussie.');
        })
      ),
    { dispatch: false }
  );

  logoutFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.logoutFailure),
        tap(() => {
          this.authService.clearAuth();
          this.router.navigate(['/login']);
          this.toast.success('Déconnexion réussie.');
        })
      ),
    { dispatch: false }
  );

  // === TOASTS ===
  // showErrorToasts$ = createEffect(
  //   () =>
  //     this.actions$.pipe(
  //       ofType(AuthActions.loginFailure, AuthActions.registerFailure),
  //       tap((response) => {
  //         const msg = response.error || 'Une erreur est survenue';
  //         this.toast.error(msg);
  //       })
  //     ),
  //   { dispatch: false }
  // );
}
